import type {
  CreateGuaranteedServiceRequest,
  CreateGuaranteedServiceResponse,
  DeleteGuaranteedServiceResponse,
  GuaranteedProbeSpendResponse,
  ListGuaranteedServiceEntriesResponse,
  ListGuaranteedServiceEventsResponse,
  ListGuaranteedServiceTextSkusResponse,
  PatchGuaranteedServiceRequest,
  PatchGuaranteedServiceResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

const ROOT = ['guaranteed-service'] as const;
// Explicit so every tenant-routing SKU pool panel that mounts on a SKU switch
// shares one read instead of refetching; writes invalidate ROOT.
const LIST_STALE_MS = 30_000;

export function useGuaranteedServiceList(): UseQueryResult<ListGuaranteedServiceEntriesResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ROOT,
    queryFn: () => opsApiRequest<ListGuaranteedServiceEntriesResponse>(
      '/v1/ops/guaranteed-service',
      {},
      fetchImpl,
    ),
    staleTime: LIST_STALE_MS,
  });
}

export interface GuaranteedEventsQuery {
  readonly page: number;
  readonly skuId: string;
  readonly upstreamId: string;
  readonly kind: string;
}

export function useGuaranteedEvents(
  params: GuaranteedEventsQuery,
): UseQueryResult<ListGuaranteedServiceEventsResponse> {
  const { fetchImpl } = useSession();
  const search = new URLSearchParams();
  search.set('page', String(params.page));
  search.set('page_size', '20');
  if (params.skuId.length > 0) search.set('sku_id', params.skuId);
  if (params.upstreamId.length > 0) search.set('upstream_id', params.upstreamId);
  if (params.kind.length > 0) search.set('kind', params.kind);
  const qs = search.toString();
  return useQuery({
    queryKey: [...ROOT, 'events', params.page, params.skuId, params.upstreamId, params.kind],
    queryFn: () => opsApiRequest<ListGuaranteedServiceEventsResponse>(
      `/v1/ops/guaranteed-service/events?${qs}`,
      {},
      fetchImpl,
    ),
  });
}

/** 探针花费 (ops read; only fetched while the page is open). */
export function useGuaranteedProbeSpend(hours: number): UseQueryResult<GuaranteedProbeSpendResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...ROOT, 'probe-spend', hours],
    queryFn: () => opsApiRequest<GuaranteedProbeSpendResponse>(
      `/v1/ops/guaranteed-service/probe-spend?hours=${String(hours)}`,
      {},
      fetchImpl,
    ),
    staleTime: LIST_STALE_MS,
  });
}

export function useGuaranteedServiceTextSkus(): UseQueryResult<ListGuaranteedServiceTextSkusResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...ROOT, 'text-skus'],
    queryFn: () => opsApiRequest<ListGuaranteedServiceTextSkusResponse>(
      '/v1/ops/guaranteed-service/text-skus',
      {},
      fetchImpl,
    ),
  });
}

export function useCreateGuaranteedService(): UseMutationResult<
  CreateGuaranteedServiceResponse,
  Error,
  CreateGuaranteedServiceRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => opsApiRequest<CreateGuaranteedServiceResponse>(
      '/v1/ops/guaranteed-service',
      { method: 'POST', body },
      fetchImpl,
    ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROOT });
    },
  });
}

export function usePatchGuaranteedService(): UseMutationResult<
  PatchGuaranteedServiceResponse,
  Error,
  { readonly id: string; readonly body: PatchGuaranteedServiceRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => opsApiRequest<PatchGuaranteedServiceResponse>(
      `/v1/ops/guaranteed-service/${encodeURIComponent(id)}`,
      { method: 'PATCH', body },
      fetchImpl,
    ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROOT });
    },
  });
}

export function useDeleteGuaranteedService(): UseMutationResult<
  DeleteGuaranteedServiceResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => opsApiRequest<DeleteGuaranteedServiceResponse>(
      `/v1/ops/guaranteed-service/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
      fetchImpl,
    ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROOT });
    },
  });
}
